// src/components/resources/tools/generators/BuyerPersona/PersonaContext.tsx

import { createContext, useState, useContext, type ReactNode, type FC, useEffect, useCallback, useRef, type RefObject, useMemo } from 'react'
import { buyerPersonaGenerator } from '~/lib/resources/data/generators/buyerPersonaGenerator'
import { type Condition, type GeneratorStep } from '~/lib/resources/types/tools/generator'
import { logClientError } from '~/lib/logger-client'

/**
 * Interface defining the structure of persona data used throughout the buyer persona generator.
 * Contains fields for both B2B and B2C personas, as well as common fields and additional
 * marketing-related information.
 * 
 * @typedef {Object} PersonaData
 * @property {string} [personaName] - Name of the persona
 * @property {string} [companyName] - Company name the persona works for
 * @property {string} [industry] - Industry the persona works in
 * @property {'B2B'|'B2C'|'B2G'|'D2C'|'Marketplace'} [businessType] - Type of business (B2B, B2C, or B2G)
 * @property {string} [jobTitleB2B] - Job title for B2B personas
 * @property {string} [responsibilitiesB2B] - Job responsibilities for B2B personas
 * @property {string[]} [valuesB2B] - Professional values for B2B personas
 * @property {string} [goalsB2B] - Professional goals for B2B personas
 * @property {string} [challengesB2B] - Professional challenges for B2B personas
 * @property {string} [b2bDecisionAuthority] - Decision-making authority for B2B personas
 * @property {string} [jobTitleB2C] - Job title for B2C personas
 * @property {string} [responsibilitiesB2C] - Job responsibilities for B2C personas
 * @property {string[]} [valuesB2C] - Personal values for B2C personas
 * @property {string} [goalsB2C] - Personal goals for B2C personas
 * @property {string} [challengesB2C] - Personal challenges for B2C personas
 * @property {string} [b2cDecisionAuthority] - Decision-making authority for B2C personas
 * @property {string} [jobTitle] - Generic job title field
 * @property {string} [responsibilities] - Generic responsibilities field
 * @property {string[]} [values] - Generic values field
 * @property {string} [goals] - Generic goals field
 * @property {string} [challenges] - Generic challenges field
 * @property {string} [decisionAuthority] - Generic decision authority field
 * @property {string} [ageRange] - Age range of the persona
 * @property {string} [gender] - Gender of the persona
 * @property {string} [income] - Income level of the persona
 * @property {string} [education] - Education level of the persona
 * @property {string} [location] - Geographic location of the persona
 * @property {string} [familyStatus] - Family status of the persona
 * @property {string[]} [interests] - Personal or professional interests
 * @property {string} [painPoints] - Pain points the persona experiences
 * @property {string[]} [researchMethods] - Methods the persona uses for research
 * @property {string} [purchaseFrequency] - How often the persona makes purchases
 * @property {string[]} [decisionCriteria] - Criteria used when making decisions
 * @property {string[]} [communicationChannels] - Preferred communication channels
 * @property {string} [brandLoyalty] - Level of brand loyalty
 * @property {string} [budgetConsciousness] - Budget consciousness level
 * @property {string[]} [platforms] - Social media or other platforms used
 * @property {string[]} [devices] - Devices used by the persona
 * @property {string[]} [contentTypes] - Content types preferred by the persona
 * @property {string} [onlineBehavior] - Online behavior patterns
 * @property {string} [digitalInfluence] - How digital channels influence decisions
 * @property {string} [metaTargeting] - Meta (Facebook) targeting information
 * @property {string} [googleTargeting] - Google targeting information
 * @property {string} [linkedinTargeting] - LinkedIn targeting information
 * @property {string} [tiktokTargeting] - TikTok targeting information
 * @property {string} [customAudiences] - Custom audience information
 * @property {string} [lookalikeStrategy] - Lookalike audience strategy
 * @property {string[]} [kpis] - Key performance indicators
 * @property {string[]} [testVariables] - Variables to test in marketing
 * @property {string} [budgetAllocation] - Budget allocation strategy
 * @property {string} [successMetrics] - Metrics to measure success
 * @property {string} [optimizationStrategies] - Strategies for optimization
 * @property {unknown} [key: string] - Allows dynamic access to fields by string key
 */
export interface PersonaData {
  personaName?: string
  companyName?: string
  industry?: string
  businessType?: 'B2B' | 'B2C' | 'D2C' | 'B2G' | 'Marketplace'
  // B2B specific fields
  jobTitleB2B?: string
  responsibilitiesB2B?: string
  valuesB2B?: string[]
  goalsB2B?: string
  challengesB2B?: string
  b2bDecisionAuthority?: string
  decisionAuthorityB2B?: string
  // B2C specific fields
  jobTitleB2C?: string
  responsibilitiesB2C?: string
  valuesB2C?: string[]
  goalsB2C?: string
  challengesB2C?: string
  b2cDecisionAuthority?: string
  decisionAuthorityB2C?: string
  // Common fields that might be renamed in the generator but map to single values
  jobTitle?: string
  responsibilities?: string
  values?: string[]
  goals?: string
  challenges?: string
  decisionAuthority?: string
  // Other common fields
  ageRange?: string
  gender?: string
  income?: string
  education?: string
  location?: string
  familyStatus?: string
  interests?: string[]
  painPoints?: string
  researchMethods?: string[]
  // Additional fields
  purchaseFrequency?: string
  decisionCriteria?: string[]
  communicationChannels?: string[]
  brandLoyalty?: string
  budgetConsciousness?: string
  platforms?: string[]
  devices?: string[]
  contentTypes?: string[]
  onlineBehavior?: string
  digitalInfluence?: string
  metaTargeting?: string
  googleTargeting?: string
  linkedinTargeting?: string
  tiktokTargeting?: string
  customAudiences?: string
  customAudienceStrategy?: string
  researchChannels?: string[] | string
  marketingChannels?: string[]
  lookalikeStrategy?: string
  lookalikeSimilarAudienceStrategy?: string
  preferredDevices?: string[]
  contentPreferences?: string
  objections?: string
  targetingRecommendations?: string
  budget?: string
  kpis?: string[]
  testVariables?: string[]
  budgetAllocation?: string
  successMetrics?: string
  optimizationStrategies?: string
  // Allow dynamic access to fields by string key
  [key: string]: unknown
}

/**
 * Interface for tracking the status of email operations in the persona generator.
 * 
 * @typedef {Object} EmailStatus
 * @property {boolean} [success] - Whether the email operation was successful
 * @property {string} [message] - Status message about the email operation
 */
interface EmailStatus {
  success?: boolean
  message?: string
}

/**
 * Checks if a value is considered empty based on its type.
 * 
 * @param {unknown} value - The value to check
 * @returns {boolean} Whether the value is empty
 */
const isEmpty = (value: unknown): boolean => {
  if (value === undefined || value === null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length === 0
  return false
}

/**
* Evaluates a condition against the current form data.
* Supports complex conditions with AND/OR logic and various comparison operators.
*
* @param {Condition} condition - The condition to evaluate
* @param {PersonaData} data - Current form data to evaluate against
* @returns {boolean} Whether the condition is satisfied
*/
const evaluateCondition = (condition: Condition, data: PersonaData): boolean => {
  const fieldValue = data[condition.field]
  
  // Handle AND conditions
  if (condition.and?.length) {
    return condition.and.every(subCondition => evaluateCondition(subCondition, data))
  }
  
  // Handle OR conditions  
  if (condition.or?.length) {
    return condition.or.some(subCondition => evaluateCondition(subCondition, data))
  }

  // Handle basic operators
  switch (condition.operator) {
    case 'equals':
      return String(fieldValue) === String(condition.value)
    case 'notEquals':
      return String(fieldValue) !== String(condition.value)
    case 'contains':
      return Array.isArray(fieldValue) 
        ? fieldValue.includes(condition.value as never)
        : String(fieldValue).includes(String(condition.value))
    case 'notContains':
      return Array.isArray(fieldValue)
        ? !fieldValue.includes(condition.value as never)
        : !String(fieldValue).includes(String(condition.value))
    case 'exists':
      return fieldValue !== undefined && fieldValue !== null && fieldValue !== ''
    case 'notExists':
      return fieldValue === undefined || fieldValue === null || fieldValue === ''
    case 'greaterThan':
      return Number(fieldValue) > Number(condition.value)
    case 'lessThan':
      return Number(fieldValue) < Number(condition.value)
    case 'greaterThanOrEquals':
      return Number(fieldValue) >= Number(condition.value)
    case 'lessThanOrEquals':
      return Number(fieldValue) <= Number(condition.value)
    case 'isEmpty':
      return isEmpty(fieldValue)
    case 'isNotEmpty':
      return !isEmpty(fieldValue)
    default:
      return false
  }
}

/**
 * Validates a generator step based on its field requirements.
 * Handles both always-required fields and conditionally required fields.
 * 
 * @param {GeneratorStep} step - The step to validate
 * @param {PersonaData} data - Current form data
 * @returns {boolean} Whether all required fields are valid
 */
const validateStep = (step: GeneratorStep, data: PersonaData): boolean => {
  const requiredFields = step.fields.filter(field => {
    // Check if the field has the required property
    if (field.required) return true
    
    // Check if the field has validation.required
    if (field.validation?.required) return true
    
    // Handle conditional requirements based on form data
    if (field.validation?.requiredIf) {
      const condition = field.validation.requiredIf
      
      // Handle array of conditions (OR logic)
      if (Array.isArray(condition)) {
        return condition.some(cond => evaluateCondition(cond as Condition, data))
      }
      
      // Handle single condition
      return evaluateCondition(condition as Condition, data)
    }
    
    return false
  })
  
  // Check if all required fields have values
  return requiredFields.every(field => {
    // Skip validation if field is not visible based on showIf condition
    if (field.showIf && !evaluateCondition(field.showIf, data)) {
      return true
    }
    
    const value = data[field.id]
    
    if (value === undefined || value === null) return false
    
    if (typeof value === 'string') return value.trim() !== ''
    if (Array.isArray(value)) return value.length > 0
    
    return !!value
  })
}

/**
 * Context type definition for the Persona generator that provides all functionality
 * and state management for the buyer persona creation process.
 * 
 * @typedef {Object} PersonaContextType
 * @property {PersonaData} formData - Current form data for the persona
 * @property {Function} updateField - Function to update a specific field in the form data
 * @property {Function} resetForm - Function to reset the form to initial state
 * @property {number} currentStep - Current step in the form process
 * @property {Function} setCurrentStep - Function to set the current step
 * @property {Record<number, boolean>} stepValidation - Validation status for each step
 * @property {boolean} showValidationErrors - Whether to show validation errors for each step
 * @property {GeneratorStep[]} steps - Array of generator steps
 * @property {boolean} previewVisible - Whether the preview panel is visible
 * @property {Function} togglePreview - Function to toggle preview visibility
 * @property {boolean} isGenerated - Whether the persona has been generated
 * @property {Function} generatePersona - Function to generate the persona
 * @property {Function} resetGenerator - Function to reset the generator
 * @property {boolean} showEmailModal - Whether the email modal is visible
 * @property {Function} setShowEmailModal - Function to set email modal visibility
 * @property {EmailStatus|null} emailStatus - Status of the email operation
 * @property {string} email - Email address to send the persona to
 * @property {Function} setEmail - Function to set the email address
 * @property {boolean} isSending - Whether an email is currently being sent
 * @property {Function} handleEmailPersona - Function to handle sending the persona via email
 * @property {Function} handleSavePersona - Function to handle saving the persona
 * @property {Function} handleDownloadPersona - Function to handle downloading the persona
 * @property {React.RefObject<HTMLDivElement | null>} personaOutputRef - Ref for the persona output
 */
interface PersonaContextType {
  // Form data
  formData: PersonaData
  updateField: (id: string, value: unknown) => void
  resetForm: () => void
  
  // Step management
  currentStep: number
  setCurrentStep: (step: number) => void
  stepValidation: Record<number, boolean>
  showValidationErrors: boolean
  steps: GeneratorStep[]
  
  // Preview
  previewVisible: boolean
  togglePreview: () => void
  
  // Output
  isGenerated: boolean
  generatePersona: () => void
  resetGenerator: () => void
  
  // Email functionality
  showEmailModal: boolean
  setShowEmailModal: (show: boolean) => void
  email: string
  setEmail: (email: string) => void
  emailStatus: EmailStatus | null
  isSending: boolean
  handleEmailPersona: (email: string) => Promise<void>
  
  // Save functionality
  handleSavePersona: () => Promise<void> | void // TODO: Implement this
  
  // Download functionality
  handleDownloadPersona: () => Promise<void>
  
  // Ref for the persona output
  personaOutputRef: RefObject<HTMLDivElement | null>
}

/**
 * Context for managing buyer persona state and operations throughout the application.
 * This context provides access to all persona data and operations.
 */
const PersonaContext = createContext<PersonaContextType | undefined>(undefined)

/**
 * Provider component that wraps the application to provide persona context.
 * Manages all state and operations related to creating, editing, and exporting
 * buyer personas.
 * 
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components that will have access to the context
 * @returns {JSX.Element} Provider component with context
 */
export const PersonaProvider: FC<{ children: ReactNode }> = ({ children }) => {
  // Get generator steps
  const steps = buyerPersonaGenerator.config.steps
  
  // Form data state
  const [formData, setFormData] = useState<PersonaData>({})
  
  // Step navigation state
  const [currentStep, setCurrentStep] = useState(0)
  const [stepValidation, setStepValidation] = useState<Record<number, boolean>>({})
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  
  // Preview state
  const [previewVisible, setPreviewVisible] = useState(true)
  
  // Generated persona state
  const [isGenerated, setIsGenerated] = useState(false)
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [email, setEmail] = useState('')
  const [emailStatus, setEmailStatus] = useState<EmailStatus | null>(null)
  const [isSending, setIsSending] = useState(false)
  
  // Add ref for persona output element
  const personaOutputRef = useRef<HTMLDivElement | null>(null)
  
  // Update a field in the form data
  const updateField = useCallback((id: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      [id]: value
    }))
  }, [])
  
  // Reset the form to initial state
  const resetForm = useCallback(() => {
    setFormData({})
    setCurrentStep(0)
    setIsGenerated(false)
    setShowValidationErrors(false)
  }, [])
  
  // Toggle preview visibility
  const togglePreview = useCallback(() => {
    setPreviewVisible(prev => !prev)
  }, [])
  
  // Validate steps when form data changes
  useEffect(() => {
    // Create a new validation object
    const newValidation: Record<number, boolean> = {}
    
    steps.forEach((step, index) => {
      newValidation[index] = validateStep(step, formData)
    })
    
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional client-side state update inside an effect (SSR-safe staging of derived UI state)
    setStepValidation(newValidation)
  }, [formData, steps])
  
  // Generate the final persona
  const generatePersona = useCallback(() => {
    if (stepValidation[steps.length - 1]) {
      setIsGenerated(true)
    } else {
      setShowValidationErrors(true)
    }
  }, [stepValidation, steps.length])
  
  // Reset the generator
  const resetGenerator = useCallback(() => {
    setFormData({})
    setCurrentStep(0)
    setIsGenerated(false)
    setStepValidation({})
    setShowValidationErrors(false)
  }, [])
  
  // Handle saving the persona
  const handleSavePersona = useCallback(() => {
    // This function is currently not implemented (real impl lands with the save flow).
  }, [])
  
  // Handle emailing the persona
  const handleEmailPersona = useCallback((_emailAddress: string): Promise<void> => {
    try {
      setIsSending(true)
      // Implementation is handled in ImageBasedPdfExport.tsx
      // This involves API calls to send the email, hence the async signature.
      // NOTE: never log `emailAddress` — it is user PII and must not reach the drain.
      setEmailStatus({ success: true, message: 'Persona sent successfully!' })
    } catch (error) {
      logClientError(error, { component: 'PersonaContext', context: 'Failed to email persona' })
      setEmailStatus({ success: false, message: 'Failed to send persona. Please try again.' })
    } finally {
      setIsSending(false)
    }
    return Promise.resolve()
  }, [])
  
  // Handle downloading the persona
  const handleDownloadPersona = useCallback((): Promise<void> => {
    try {
      // Implementation is handled in ImageBasedPdfExport.tsx
      // This may involve async operations like PDF generation or image processing.
    } catch (error) {
      logClientError(error, { component: 'PersonaContext', context: 'Failed to download persona' })
    }
    return Promise.resolve()
  }, [])
  
  // Memoize the context value to prevent unnecessary re-renders of consuming components
  // This is important for performance as it prevents child components from re-rendering
  // when the provider re-renders but the actual context values haven't changed
  const contextValue: PersonaContextType = useMemo(() => ({
    // Form data
    formData,
    updateField,
    resetForm,
    
    // Step management
    currentStep,
    setCurrentStep,
    stepValidation,
    showValidationErrors,
    steps,
    
    // Preview
    previewVisible,
    togglePreview,
    
    // Output
    isGenerated,
    generatePersona,
    resetGenerator,
    
    // Email functionality
    showEmailModal,
    setShowEmailModal,
    email,
    setEmail,
    emailStatus,
    isSending,
    handleEmailPersona,
    
    // Save functionality
    handleSavePersona,
    
    // Download functionality
    handleDownloadPersona,
    
    // Ref for the persona output
    personaOutputRef
  }), [
    formData,
    updateField,
    resetForm,
    currentStep,
    setCurrentStep,
    stepValidation,
    showValidationErrors,
    steps,
    previewVisible,
    togglePreview,
    isGenerated,
    generatePersona,
    resetGenerator,
    showEmailModal,
    setShowEmailModal,
    email,
    setEmail,
    emailStatus,
    isSending,
    handleEmailPersona,
    handleSavePersona,
    handleDownloadPersona,
    personaOutputRef
  ])
  
  return (
    <PersonaContext.Provider value={contextValue}>
      {children}
    </PersonaContext.Provider>
  )
}

/**
 * Custom hook to access the persona context from any component.
 * Must be used within a component that is a child of PersonaProvider.
 * 
 * @returns {PersonaContextType} The persona context with all state and functions
 * @throws {Error} If used outside of PersonaProvider
 */
export const usePersona = (): PersonaContextType => {
  const context = useContext(PersonaContext)
  if (context === undefined) {
    throw new Error('usePersona must be used within a PersonaProvider')
  }
  return context
}